import React, { useMemo } from 'react';
import useSWR from 'swr';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { getLayoutDetail } from '@/api/analytics';
import { Tooltip } from '@/components/ui/tooltip';
import {
  parseBody,
  dividerLabel,
  isTreeFormat,
  type TypedLine,
  type LongestQueueLine,
  type LongQueueLine,
  type NoVisitorsLine,
} from '../constructor/lineModel';
import { ReadOnlyBody } from '../ReadOnlyBody';
import { FoodStatusChartBlock } from './FoodStatusChartBlock';
import { KeypointChartBlock } from './KeypointChartBlock';
import { QueueChartBlock } from './QueueChartBlock';
import { CategoryTreeChartBlock } from './CategoryTreeChartBlock';
import { TreeQueueChartBlock } from './TreeQueueChartBlock';

function rowHtml(row: string): string {
  return row
    .replace(/^<blockquote>/, '')
    .replace(/<\/blockquote>$/, '')
    .replace(/<a\s+/gi, '<a target="_blank" rel="noreferrer" ');
}

// item 5: the TG "cap" (🏁 Итоги дня / Филиал / Период / Дашборды) is noise
// here — the shared header above the editor already names the branch/day,
// and this view's whole point is the per-line breakdown below it, not a
// second copy of the summary block. Same prefixes ReportBody.tsx already
// strips for the read-only /usage/report view. 'Дата:'/'Полный отчет:' are
// the tree-format rewrite's replacements for 'Период:'/the old bottom link.
const CAP_PREFIXES = ['🏁', 'Филиал:', 'Период:', 'Дата:', 'Полный отчет:', 'Дашборды:', 'Здесь могла бы быть'];
function isCapLine(text: string): boolean {
  const stripped = text.replace(/<[^>]+>/g, '').trim();
  return CAP_PREFIXES.some((p) => stripped.startsWith(p));
}

/**
 * V3 — Интерактив tab. Same per-line classification as `ConstructorView`, but
 * instead of a click-to-open form, the modelled lines get their annotatable
 * chart(s) mounted INLINE, directly below — per Alexey's verbatim spec
 * ("right below this should show chart..."). Segment/keypoint/queue-event
 * geometry is session-local state (not yet persisted to `ReportFacts` — that
 * BE model is still V3-substrate/deferred, see FRAMEWORK.md §1); edits write
 * straight back into `body_html` lines the same way the V2 forms do, so
 * Конструктор and Интерактив stay two interchangeable ways to edit the same
 * lines.
 */
export function InteractiveView({
  value,
  onChange,
  day,
  zoneId,
  openTime,
  maxCashiers,
  hiddenSections,
  onToggleSection,
  disabled,
}: {
  value: string;
  onChange: (bodyHtml: string) => void;
  day: string;
  zoneId: string;
  openTime: string | null;
  maxCashiers: number | null;
  hiddenSections: Set<string>;
  onToggleSection: (key: string) => void;
  disabled?: boolean;
}) {
  const { t } = useT();
  const treeFormat = useMemo(() => isTreeFormat(value), [value]);
  const lines = useMemo(() => parseBody(value, openTime), [value, openTime]);
  const rows = useMemo(() => value.split('\n'), [value]);

  // Fresh rack list WITH ids (racksByType from the parent only carries name+url —
  // the per-rack charts need the id to fetch layout-detail). Skipped for a
  // legacy body — read-only there, no chart data needed.
  const { data: layout } = useSWR(
    zoneId && treeFormat ? ['rv-interactive-racks', zoneId, day] : null,
    () => getLayoutDetail(zoneId, day, day).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  const racksByType = useMemo(() => {
    const out: Record<string, { id: number; name: string }[]> = {};
    for (const rack of layout?.racks ?? []) {
      const type = rack.rack_type ?? '';
      (out[type] ??= []).push({ id: rack.id, name: rack.name });
    }
    return out;
  }, [layout]);

  const queueLines = useMemo(
    () => lines.filter((l): l is TypedLine => l.kind === 'typed'),
    [lines],
  );
  const firstQueueIdx = useMemo(() => lines.findIndex((l) => l.kind === 'typed'), [lines]);
  const firstTreeQueueIdx = useMemo(
    () => lines.findIndex((l) => l.kind === 'longest_queue' || l.kind === 'long_queue' || l.kind === 'no_visitors'),
    [lines],
  );

  // Alexey (2026-09-03, direct instruction): legacy formats become
  // view-only — no inline charts here, just the formatted text.
  if (!treeFormat) {
    return (
      <ReadOnlyBody
        value={value}
        hiddenSections={hiddenSections}
        onToggleSection={onToggleSection}
        disabled={disabled}
      />
    );
  }

  let curKey = '';
  let curHidden = false;

  return (
    <div className="report-body flex flex-col gap-3 rounded-md border border-border bg-background p-3 text-sm leading-relaxed">
      {lines.map((line, li) => {
        if (line.kind === 'other' && line.isDivider) {
          curKey = dividerLabel(line.text);
          curHidden = hiddenSections.has(curKey);
          const key = curKey;
          return (
            <div key={li} className="mb-1 mt-3 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span
                className={cn(
                  'text-[11px] font-semibold uppercase tracking-wider',
                  curHidden ? 'text-muted-foreground/50 line-through' : 'text-muted-foreground',
                )}
              >
                {key}
              </span>
              {/* item 13: fast custom tooltip (not the native `title=`, which
                  is slow to appear) explaining what the eye toggles. */}
              <Tooltip
                content={t(curHidden ? 'reportValidation.constructor.unhide' : 'reportValidation.constructor.hide')}
                openDelay={100}
              >
                <button
                  type="button"
                  onClick={() => onToggleSection(key)}
                  disabled={disabled}
                  aria-label={t(curHidden ? 'reportValidation.constructor.unhide' : 'reportValidation.constructor.hide')}
                  className={cn(
                    'shrink-0 rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground',
                    disabled && 'pointer-events-none opacity-30',
                  )}
                >
                  {curHidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                </button>
              </Tooltip>
              <span className="h-px flex-1 bg-border" />
            </div>
          );
        }

        if (curHidden) return null;

        // ── F1: Выкладка status line + one chart per rack of the type ──
        if (line.kind === 'food_status') {
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            // item 15: one clear card per finding — text on top, a plain
            // (unlabelled) divider, then the interactive controls.
            <div key={`food:${li}:${line.fields.rackType}`} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
              <div>
                {span.map((row, i) =>
                  row.startsWith('<blockquote') ? (
                    <blockquote key={i} className="mt-0.5" dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                  ) : (
                    <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                  ),
                )}
              </div>
              <div className="h-px bg-border" />
              <FoodStatusChartBlock
                line={line}
                racks={racksByType[line.fields.rackType] ?? []}
                zoneId={zoneId}
                day={day}
                value={value}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
          );
        }

        // ── F2: ключевые точки block + the SAME per-rack charts, keypoint mode ──
        if (line.kind === 'keypoint_block') {
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          const rackType = line.block.name;
          return (
            // item 15: one clear card per finding — KeypointChartBlock now
            // renders each report LINE as its own colour-linked mini-card
            // (items 16-19) instead of this dumping the whole span as one
            // undifferentiated paragraph above the chart.
            <div key={`kp:${li}:${rackType || line.block.gen}`} className="rounded-xl border border-border bg-card p-3">
              <KeypointChartBlock
                line={line}
                racks={racksByType[rackType] ?? []}
                zoneId={zoneId}
                day={day}
                value={value}
                onChange={onChange}
                spanRows={span.map(rowHtml)}
                disabled={disabled}
              />
            </div>
          );
        }

        // ── F3: очередь и бар — one shared chart under the whole 3-line group ──
        if (line.kind === 'typed') {
          if (li !== firstQueueIdx) return null;
          return (
            // item 15: one clear card per finding, same pattern as F1/F2.
            <div key="queue" className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
              <div>
                {queueLines.map((l, i) => (
                  <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(rows[l.startIdx] ?? '') }} />
                ))}
              </div>
              <div className="h-px bg-border" />
              <QueueChartBlock
                lines={queueLines}
                zoneId={zoneId}
                day={day}
                value={value}
                onChange={onChange}
                maxCashiers={maxCashiers}
                disabled={disabled}
              />
            </div>
          );
        }

        // ── Tree-format "Выкладка" category block → its own chart card ──
        if (line.kind === 'category_tree') {
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            <div key={`cat:${li}:${line.fields.rackType}`} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
              <div>
                {span.map((row, i) => (
                  <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                ))}
              </div>
              {line.fields.variant === 'issues' && (
                <>
                  <div className="h-px bg-border" />
                  <CategoryTreeChartBlock
                    line={line}
                    racks={racksByType[line.fields.rackType] ?? []}
                    zoneId={zoneId}
                    day={day}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </>
              )}
            </div>
          );
        }

        // ── Tree-format "Очередь и бар" tail — one shared chart card, same
        // consolidation pattern as F3's `typed` group above. ──
        if (line.kind === 'longest_queue' || line.kind === 'long_queue' || line.kind === 'no_visitors') {
          if (li !== firstTreeQueueIdx) return null;
          const longestLine = lines.find((l): l is LongestQueueLine => l.kind === 'longest_queue') ?? null;
          const longQueueLine = lines.find((l): l is LongQueueLine => l.kind === 'long_queue') ?? null;
          const noVisitorsLine = lines.find((l): l is NoVisitorsLine => l.kind === 'no_visitors') ?? null;
          return (
            <div key="tree-queue" className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
              <div>
                {longQueueLine && (
                  <div dangerouslySetInnerHTML={{ __html: rowHtml(rows[longQueueLine.startIdx] ?? '') }} />
                )}
                {longestLine &&
                  rows
                    .slice(longestLine.startIdx, longestLine.endIdx + 1)
                    .map((row, i) => <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />)}
                {noVisitorsLine && (
                  <div dangerouslySetInnerHTML={{ __html: rowHtml(rows[noVisitorsLine.startIdx] ?? '') }} />
                )}
              </div>
              <div className="h-px bg-border" />
              <TreeQueueChartBlock
                longestLine={longestLine}
                longQueueLine={longQueueLine}
                noVisitorsLine={noVisitorsLine}
                zoneId={zoneId}
                day={day}
                value={value}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
          );
        }

        // ── Tree-format "Чеклист" section — text only, no chart. A
        // comma-separated task list has no time-series/numeric shape to
        // annotate (unlike Выкладка/Очередь, which chart occupancy/queue
        // samples over the day), so this stays a plain card here; it's
        // still fully editable via Конструктор's ChecklistForm. ──
        if (line.kind === 'checklist') {
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            <div key={`cl:${li}`} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
              <div>
                {span.map((row, i) => (
                  <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                ))}
              </div>
            </div>
          );
        }

        // ── Unmodelled lines: plain text (no chart — edit via Конструктор/Форматирование) ──
        if (line.kind === 'other' && line.isBlank) return <div key={li} className="h-2" />;
        const text = line.kind === 'other' || line.kind === 'highlighted' ? line.text : '';
        // item 5: the cap's "🏁 Итоги дня 🏁" title line is <b>-wrapped, so it
        // parses as 'highlighted' (bold values), not 'other' — check both.
        if ((line.kind === 'other' || line.kind === 'highlighted') && isCapLine(text)) return null;
        return <div key={li} className="py-0.5" dangerouslySetInnerHTML={{ __html: rowHtml(text) }} />;
      })}
    </div>
  );
}
